import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import useErroPadrao from "../../../hooks/useErroPadrao";
import useFlashMessage from "../../../hooks/useFlashMessages";
import useContabilidade from "../../../hooks/useContabilidade";
import { CHAVE_TOKEN, real, dataBR, hojeISO } from "../../../utils/formata";

const VERDE = "#2ecc71";
const VERMELHO = "#ff6b5b";
const DOURADO = "#d4af37";
const CINZA = "#9aa5b5";

const CHAVE_ABA = "help-aba-contabilidade";

const CATEGORIAS = [
    { valor: "vendas", rotulo: "Vendas / Serviços", tipo: "receita" },
    { valor: "juros", rotulo: "Juros e encargos", tipo: "receita" },
    { valor: "aporte", rotulo: "Aporte de capital", tipo: "receita" },
    { valor: "outras_receitas", rotulo: "Outras receitas", tipo: "receita" },
    { valor: "fornecedores", rotulo: "Fornecedores", tipo: "despesa" },
    { valor: "aluguel", rotulo: "Aluguel", tipo: "despesa" },
    { valor: "salarios", rotulo: "Salários / Comissões", tipo: "despesa" },
    { valor: "impostos", rotulo: "Impostos / Taxas", tipo: "despesa" },
    { valor: "marketing", rotulo: "Marketing / Anúncios", tipo: "despesa" },
    { valor: "combustivel", rotulo: "Combustível / Transporte", tipo: "despesa" },
    { valor: "material", rotulo: "Material / Equipamento", tipo: "despesa" },
    { valor: "outras_despesas", rotulo: "Outras despesas", tipo: "despesa" },
];

// Espelha a whitelist do backend (FORMAS_PAGAMENTO no contabilidadeController).
// Valor fora dessa lista volta 422 e o lançamento não salva.
const FORMAS = [
    { valor: "pix", rotulo: "Pix" },
    { valor: "dinheiro", rotulo: "Dinheiro" },
    { valor: "transferencia", rotulo: "Transferência" },
    { valor: "boleto", rotulo: "Boleto" },
    { valor: "cartao", rotulo: "Cartão" },
];

const CAT_PADRAO = { receita: "vendas", despesa: "fornecedores" };

const rotuloCategoria = (c) => CATEGORIAS.find((x) => x.valor === c)?.rotulo || c || "—";
const rotuloForma = (f) => FORMAS.find((x) => x.valor === f)?.rotulo || f || "—";

// Registro antigo com forma que o select não tem faria o <select> mostrar "Pix"
// e o state mandar o valor velho — 422 na hora de salvar
const formaValida = (f) => (FORMAS.some((x) => x.valor === f) ? f : "pix");

// "1.234,56" e "1234,56" viram 1234.56 — o backend faz Number(valor) puro e
// qualquer vírgula viraria NaN ("Informe um valor maior que zero")
function numeroBR(valor) {
    if (typeof valor === "number") return valor;
    const texto = String(valor ?? "").replace(/\s|R\$/g, "");
    if (!texto) return NaN;
    return Number(texto.includes(",") ? texto.replace(/\./g, "").replace(",", ".") : texto);
}

const MES_ATUAL = hojeISO().slice(0, 7);

function ultimoDiaDoMes(mes) {
    const [ano, m] = String(mes).split("-").map(Number);
    return `${mes}-${String(new Date(ano, m, 0).getDate()).padStart(2, "0")}`;
}

function mesAnteriorDe(mes) {
    const [ano, m] = String(mes).split("-").map(Number);
    const d = new Date(ano, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function nomeDoMes(mes) {
    const [ano, m] = String(mes).split("-").map(Number);
    const nome = new Date(ano, m - 1, 1).toLocaleDateString("pt-BR", { month: "long" });
    return `${nome.charAt(0).toUpperCase()}${nome.slice(1)} de ${ano}`;
}

// null = sem base de comparação (mês anterior zerado)
function variacao(atual, anterior) {
    const base = Number(anterior || 0);
    const agora = Number(atual || 0);
    if (base === 0) return null;
    return ((agora - base) / Math.abs(base)) * 100;
}

// sem sinal: quem mostra a direção é a seta ▲/▼ do comparativo
const pct = (n) => `${Math.abs(n).toFixed(1).replace(".", ",")}%`;

const LANCAMENTO_VAZIO = {
    tipo: "receita",
    descricao: "",
    valor: "",
    categoria: "vendas",
    data: hojeISO(),
    formaPagamento: "pix",
    observacao: "",
};

const CONTA_VAZIA = {
    tipo: "pagar",
    descricao: "",
    valor: "",
    vencimento: hojeISO(),
    categoria: "fornecedores",
    pessoa: "",
    recorrente: false,
    observacao: "",
};

export default function ContabilidadePainel() {
    const [aba, setAba] = useState(() => localStorage.getItem(CHAVE_ABA) || "lancamentos");

    // ===== Lançamentos =====
    const [filtros, setFiltros] = useState({
        de: `${MES_ATUAL}-01`,
        ate: ultimoDiaDoMes(MES_ATUAL),
        tipo: "",
        categoria: "",
        busca: "",
    });
    const [busca, setBusca] = useState("");
    const [dados, setDados] = useState({ lancamentos: [], totais: { receitas: 0, despesas: 0, saldo: 0 } });
    const [lancamento, setLancamento] = useState(LANCAMENTO_VAZIO);
    const [editandoLancamento, setEditandoLancamento] = useState(null);
    const [carregandoLanc, setCarregandoLanc] = useState(true);
    const [salvandoLanc, setSalvandoLanc] = useState(false);

    // ===== Contas =====
    const [filtrosConta, setFiltrosConta] = useState({ tipo: "", status: "aberta" });
    const [contas, setContas] = useState({
        contas: [],
        resumo: { aPagar: 0, aReceber: 0, vencidas: 0, venceHoje: 0, venceSemana: 0 },
    });
    const [conta, setConta] = useState(CONTA_VAZIA);
    const [editandoConta, setEditandoConta] = useState(null);
    const [contaBaixada, setContaBaixada] = useState(false);
    const [baixa, setBaixa] = useState(null);
    const [carregandoContas, setCarregandoContas] = useState(true);
    const [salvandoConta, setSalvandoConta] = useState(false);
    const [dandoBaixa, setDandoBaixa] = useState(false);

    // ===== Fechamento =====
    const [mes, setMes] = useState(MES_ATUAL);
    const [fechamento, setFechamento] = useState(null);
    const [carregandoFech, setCarregandoFech] = useState(true);

    const {
        listarLancamentos, criarLancamento, atualizarLancamento, excluirLancamento,
        listarContas, criarConta, atualizarConta, excluirConta, baixarConta, estornarConta,
        consultarFechamento,
    } = useContabilidade();
    const { setErroPadrao } = useErroPadrao();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem(CHAVE_TOKEN)) navigate("/login");
    }, [navigate]);

    useEffect(() => {
        localStorage.setItem(CHAVE_ABA, aba);
    }, [aba]);

    // Digitar na busca não pode disparar uma request por tecla
    useEffect(() => {
        const timer = setTimeout(() => {
            setFiltros((f) => (f.busca === busca ? f : { ...f, busca }));
        }, 400);
        return () => clearTimeout(timer);
    }, [busca]);

    // Mudar filtro rápido (busca com debounce, clique nos cards) deixa duas
    // requisições no ar — sem o selo a resposta velha pode chegar por último e
    // pintar totais que não são os do filtro que está na tela
    const pedidoLanc = useRef(0);
    const pedidoContas = useRef(0);
    const pedidoFech = useRef(0);

    const carregarLancamentos = useCallback(() => {
        const meu = ++pedidoLanc.current;
        setCarregandoLanc(true);
        listarLancamentos(filtros)
            .then((r) => {
                if (meu !== pedidoLanc.current) return;
                setDados({
                    lancamentos: r.lancamentos || [],
                    totais: r.totais || { receitas: 0, despesas: 0, saldo: 0 },
                });
            })
            .catch((error) => { if (meu === pedidoLanc.current) setErroPadrao(error); })
            .finally(() => { if (meu === pedidoLanc.current) setCarregandoLanc(false); });
    }, [filtros, listarLancamentos, setErroPadrao]);

    const carregarContas = useCallback(() => {
        const meu = ++pedidoContas.current;
        setCarregandoContas(true);
        listarContas(filtrosConta)
            .then((r) => {
                if (meu !== pedidoContas.current) return;
                setContas({
                    contas: r.contas || [],
                    resumo: r.resumo || { aPagar: 0, aReceber: 0, vencidas: 0, venceHoje: 0, venceSemana: 0 },
                });
            })
            .catch((error) => { if (meu === pedidoContas.current) setErroPadrao(error); })
            .finally(() => { if (meu === pedidoContas.current) setCarregandoContas(false); });
    }, [filtrosConta, listarContas, setErroPadrao]);

    const carregarFechamento = useCallback(() => {
        const meu = ++pedidoFech.current;
        setCarregandoFech(true);
        consultarFechamento(mes)
            .then((r) => { if (meu === pedidoFech.current) setFechamento(r); })
            .catch((error) => { if (meu === pedidoFech.current) setErroPadrao(error); })
            .finally(() => { if (meu === pedidoFech.current) setCarregandoFech(false); });
    }, [mes, consultarFechamento, setErroPadrao]);

    useEffect(() => { if (aba === "lancamentos") carregarLancamentos(); }, [aba, carregarLancamentos]);
    useEffect(() => { if (aba === "contas") carregarContas(); }, [aba, carregarContas]);
    useEffect(() => { if (aba === "fechamento") carregarFechamento(); }, [aba, carregarFechamento]);

    // ===== Ações: lançamentos =====
    function mudarTipoLancamento(tipo) {
        setLancamento((l) => {
            const atual = CATEGORIAS.find((c) => c.valor === l.categoria);
            return { ...l, tipo, categoria: atual && atual.tipo === tipo ? l.categoria : CAT_PADRAO[tipo] };
        });
    }

    function handleSalvarLancamento(e) {
        e.preventDefault();
        const valor = numeroBR(lancamento.valor);
        if (!Number.isFinite(valor) || valor <= 0) {
            setFlashMessage("Informe um valor maior que zero.", "error");
            return;
        }
        setSalvandoLanc(true);
        const corpo = { ...lancamento, valor };
        const acao = editandoLancamento
            ? atualizarLancamento(editandoLancamento, corpo)
            : criarLancamento(corpo);
        acao
            .then((r) => {
                setFlashMessage(r.message || (editandoLancamento ? "Lançamento atualizado!" : "Lançamento registrado!"), "success");
                setLancamento({ ...LANCAMENTO_VAZIO, tipo: lancamento.tipo, categoria: lancamento.categoria, data: lancamento.data });
                setEditandoLancamento(null);
                carregarLancamentos();
            })
            .catch((error) => setErroPadrao(error))
            .finally(() => setSalvandoLanc(false));
    }

    function editarLancamento(l) {
        setEditandoLancamento(l.id);
        setLancamento({
            tipo: l.tipo || "receita",
            descricao: l.descricao || "",
            valor: l.valor ?? "",
            categoria: l.categoria || CAT_PADRAO[l.tipo] || "vendas",
            data: String(l.data || hojeISO()).slice(0, 10),
            formaPagamento: formaValida(l.formaPagamento),
            observacao: l.observacao || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function cancelarEdicaoLancamento() {
        setEditandoLancamento(null);
        setLancamento(LANCAMENTO_VAZIO);
    }

    function handleExcluirLancamento(l) {
        if (!window.confirm(`Excluir o lançamento "${l.descricao}" de ${real(l.valor)}?`)) return;
        excluirLancamento(l.id)
            .then((r) => {
                setFlashMessage(r.message || "Lançamento excluído!", "success");
                if (editandoLancamento === l.id) cancelarEdicaoLancamento();
                carregarLancamentos();
            })
            .catch((error) => setErroPadrao(error));
    }

    // ===== Ações: contas =====
    function mudarTipoConta(tipo) {
        setConta((c) => {
            const atual = CATEGORIAS.find((x) => x.valor === c.categoria);
            const alvo = tipo === "pagar" ? "despesa" : "receita";
            return { ...c, tipo, categoria: atual && atual.tipo === alvo ? c.categoria : CAT_PADRAO[alvo] };
        });
    }

    function handleSalvarConta(e) {
        e.preventDefault();
        const valor = numeroBR(conta.valor);
        if (!contaBaixada && (!Number.isFinite(valor) || valor <= 0)) {
            setFlashMessage("Informe um valor maior que zero.", "error");
            return;
        }
        setSalvandoConta(true);
        // conta baixada trava tipo/valor/vencimento no backend (422) — manda só
        // o que ela ainda deixa mudar
        const corpo = contaBaixada
            ? {
                descricao: conta.descricao,
                categoria: conta.categoria,
                pessoa: conta.pessoa,
                recorrente: conta.recorrente,
                observacao: conta.observacao,
            }
            : { ...conta, valor };
        const acao = editandoConta ? atualizarConta(editandoConta, corpo) : criarConta(corpo);
        acao
            .then((r) => {
                setFlashMessage(r.message || (editandoConta ? "Conta atualizada!" : "Conta cadastrada!"), "success");
                setConta({ ...CONTA_VAZIA, tipo: conta.tipo, categoria: conta.categoria });
                setEditandoConta(null);
                setContaBaixada(false);
                carregarContas();
            })
            .catch((error) => setErroPadrao(error))
            .finally(() => setSalvandoConta(false));
    }

    function editarConta(c) {
        setEditandoConta(c.id);
        setContaBaixada(c.status === "paga");
        setBaixa(null);
        setConta({
            tipo: c.tipo || "pagar",
            descricao: c.descricao || "",
            valor: c.valor ?? "",
            vencimento: String(c.vencimento || hojeISO()).slice(0, 10),
            categoria: c.categoria || "fornecedores",
            pessoa: c.pessoa || "",
            recorrente: !!c.recorrente,
            observacao: c.observacao || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function cancelarEdicaoConta() {
        setEditandoConta(null);
        setContaBaixada(false);
        setConta(CONTA_VAZIA);
    }

    function abrirBaixa(c) {
        // abrir a baixa sem fechar a edição deixava dois formulários mandando na
        // mesma conta — editarConta já faz o inverso
        cancelarEdicaoConta();
        setBaixa({
            id: c.id,
            data: hojeISO(),
            valorPago: numeroBR(c.valor),
            formaPagamento: "pix",
        });
    }

    function handleBaixar(e) {
        e.preventDefault();
        const valorPago = numeroBR(baixa.valorPago);
        if (!Number.isFinite(valorPago) || valorPago <= 0) {
            setFlashMessage("Informe um valor pago maior que zero.", "error");
            return;
        }
        setDandoBaixa(true);
        const { id, ...corpo } = baixa;
        baixarConta(id, { ...corpo, valorPago })
            .then((r) => {
                setFlashMessage(r.message || "Baixa registrada — já entrou nos lançamentos!", "success");
                setBaixa(null);
                carregarContas();
            })
            .catch((error) => setErroPadrao(error))
            .finally(() => setDandoBaixa(false));
    }

    function handleEstornar(c) {
        if (!window.confirm(`Estornar a baixa de "${c.descricao}"? O lançamento gerado também sai da contabilidade.`)) return;
        estornarConta(c.id)
            .then((r) => {
                setFlashMessage(r.message || "Baixa estornada!", "success");
                carregarContas();
            })
            .catch((error) => setErroPadrao(error));
    }

    function handleExcluirConta(c) {
        if (!window.confirm(`Excluir a conta "${c.descricao}" de ${real(c.valor)}?`)) return;
        excluirConta(c.id)
            .then((r) => {
                setFlashMessage(r.message || "Conta excluída!", "success");
                if (editandoConta === c.id) cancelarEdicaoConta();
                if (baixa?.id === c.id) setBaixa(null);
                carregarContas();
            })
            .catch((error) => setErroPadrao(error));
    }

    // ===== Estilos reaproveitados =====
    const rotulo = { fontSize: "0.78em", marginBottom: 2 };
    const chip = {
        background: "rgba(212, 175, 55, 0.12)",
        border: "1px solid rgba(212, 175, 55, 0.35)",
        color: DOURADO,
        borderRadius: 20,
        padding: "2px 10px",
        fontSize: "0.75em",
        fontWeight: 600,
        whiteSpace: "nowrap",
    };

    function botaoTipo(ativo, cor, onClick, icone, texto, desabilitado) {
        return (
            <button type="button" onClick={onClick} disabled={desabilitado}
                className={ativo ? "btn btn-he" : "btn btn-outline-he"}
                style={{
                    flex: "1 1 120px", fontWeight: 700, padding: "0.6em 0.4em",
                    ...(ativo ? {} : { borderColor: `${cor}66`, color: cor }),
                }}>
                <i className={icone}></i> {texto}
            </button>
        );
    }

    function cardValor(titulo, valor, cor, extra) {
        return (
            <div className={extra?.classe ? `card-estat ${extra.classe}` : "card-estat"}
                style={{ textAlign: "center", ...(extra?.estilo || {}) }}
                onClick={extra?.onClick}
                title={extra?.title}>
                <div className="estat-rotulo" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                    {titulo}
                </div>
                <div className="estat-numero" style={{ color: cor, fontWeight: 800 }}>{valor}</div>
                {extra?.rodape}
            </div>
        );
    }

    // ===== ABA LANÇAMENTOS =====
    function renderLancamentos() {
        const t = dados.totais || {};
        const saldo = Number(t.saldo || 0);
        const catsDoTipo = (tipo) => CATEGORIAS.filter((c) => c.tipo === tipo);

        return (
            <>
                <div className="cards-estatistica" style={{ margin: "1.2em 0" }}>
                    {cardValor("Receitas", real(t.receitas), VERDE, {
                        onClick: () => setFiltros((f) => ({ ...f, tipo: f.tipo === "receita" ? "" : "receita" })),
                        title: "Clique pra filtrar só receitas",
                        estilo: {
                            cursor: "pointer",
                            border: filtros.tipo === "receita" ? `1px solid ${VERDE}` : undefined,
                            boxShadow: filtros.tipo === "receita" ? `0 0 14px ${VERDE}55` : undefined,
                        },
                    })}
                    {cardValor("Despesas", real(t.despesas), VERMELHO, {
                        onClick: () => setFiltros((f) => ({ ...f, tipo: f.tipo === "despesa" ? "" : "despesa" })),
                        title: "Clique pra filtrar só despesas",
                        estilo: {
                            cursor: "pointer",
                            border: filtros.tipo === "despesa" ? `1px solid ${VERMELHO}` : undefined,
                            boxShadow: filtros.tipo === "despesa" ? `0 0 14px ${VERMELHO}55` : undefined,
                        },
                    })}
                    {cardValor("Saldo do período", real(saldo), saldo >= 0 ? VERDE : VERMELHO, {
                        classe: "largura-total",
                        estilo: { border: `1px solid ${DOURADO}66` },
                        rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>{dataBR(filtros.de)} a {dataBR(filtros.ate)}</div>,
                    })}
                </div>

                {/* Filtros */}
                <div className="he-card form-mobile" style={{ marginBottom: "1em" }}>
                    <div className="row" style={{ alignItems: "flex-end" }}>
                        <div className="col-md-2" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>De</label>
                            <input className="form-control form-control-sm" type="date" value={filtros.de}
                                onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} />
                        </div>
                        <div className="col-md-2" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Até</label>
                            <input className="form-control form-control-sm" type="date" value={filtros.ate}
                                onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} />
                        </div>
                        <div className="col-md-2" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Tipo</label>
                            <select className="form-select form-select-sm" value={filtros.tipo}
                                onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}>
                                <option value="">Todos</option>
                                <option value="receita">Receitas</option>
                                <option value="despesa">Despesas</option>
                            </select>
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Categoria</label>
                            <select className="form-select form-select-sm" value={filtros.categoria}
                                onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}>
                                <option value="">Todas</option>
                                <optgroup label="Receitas">
                                    {catsDoTipo("receita").map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
                                </optgroup>
                                <optgroup label="Despesas">
                                    {catsDoTipo("despesa").map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
                                </optgroup>
                            </select>
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Buscar</label>
                            <input className="form-control form-control-sm" placeholder="Descrição do lançamento..."
                                value={busca} onChange={(e) => setBusca(e.target.value)} />
                        </div>
                    </div>
                    <button className="btn btn-sm btn-outline-he btn-total-mobile" type="button"
                        onClick={() => {
                            setBusca("");
                            setFiltros({ de: `${MES_ATUAL}-01`, ate: ultimoDiaDoMes(MES_ATUAL), tipo: "", categoria: "", busca: "" });
                        }}>
                        <i className="bi bi-arrow-counterclockwise"></i> Limpar
                    </button>
                </div>

                {/* Novo lançamento */}
                <form onSubmit={handleSalvarLancamento} className="he-card form-mobile" style={{ marginBottom: "1em", display: "flex", flexDirection: "column", gap: 12 }}>
                    <h5 className="he-titulo he-dourado" style={{ margin: 0 }}>
                        <i className={editandoLancamento ? "bi bi-pencil-square" : "bi bi-plus-circle"}></i>{" "}
                        {editandoLancamento ? "Editando lançamento" : "Novo lançamento"}
                    </h5>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {botaoTipo(lancamento.tipo === "receita", VERDE, () => mudarTipoLancamento("receita"), "bi bi-arrow-down-circle", "Receita")}
                        {botaoTipo(lancamento.tipo === "despesa", VERMELHO, () => mudarTipoLancamento("despesa"), "bi bi-arrow-up-circle", "Despesa")}
                    </div>

                    <div className="row">
                        <div className="col-md-4" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Descrição</label>
                            <input className="form-control form-control-sm" placeholder="Ex: Mensalidade do cliente Padaria Pão Quente"
                                value={lancamento.descricao}
                                onChange={(e) => setLancamento({ ...lancamento, descricao: e.target.value })} required />
                        </div>
                        <div className="col-md-2" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Valor</label>
                            <input className="form-control form-control-sm" type="number" step="0.01" min="0.01" placeholder="0,00"
                                value={lancamento.valor}
                                onChange={(e) => setLancamento({ ...lancamento, valor: e.target.value })} required />
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Categoria</label>
                            <select className="form-select form-select-sm" value={lancamento.categoria}
                                onChange={(e) => setLancamento({ ...lancamento, categoria: e.target.value })}>
                                <optgroup label="Receitas">
                                    {catsDoTipo("receita").map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
                                </optgroup>
                                <optgroup label="Despesas">
                                    {catsDoTipo("despesa").map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
                                </optgroup>
                            </select>
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Data</label>
                            <input className="form-control form-control-sm" type="date" value={lancamento.data}
                                onChange={(e) => setLancamento({ ...lancamento, data: e.target.value })} required />
                        </div>
                        <div className="col-md-4" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Forma de pagamento</label>
                            <select className="form-select form-select-sm" value={lancamento.formaPagamento}
                                onChange={(e) => setLancamento({ ...lancamento, formaPagamento: e.target.value })}>
                                {FORMAS.map((f) => <option key={f.valor} value={f.valor}>{f.rotulo}</option>)}
                            </select>
                        </div>
                        <div className="col-md-8" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Observação (opcional)</label>
                            <input className="form-control form-control-sm" placeholder="Nota fiscal, nº do documento, detalhe do acerto..."
                                value={lancamento.observacao}
                                onChange={(e) => setLancamento({ ...lancamento, observacao: e.target.value })} />
                        </div>
                    </div>

                    <div className="acoes-linha">
                        <button type="submit" className="btn btn-he" style={{ flex: "1 1 200px" }} disabled={salvandoLanc}>
                            {salvandoLanc
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <><i className="bi bi-check-lg"></i> {editandoLancamento ? "Salvar alteração" : "Lançar"}</>}
                        </button>
                        {editandoLancamento &&
                            <button type="button" className="btn btn-outline-he" onClick={cancelarEdicaoLancamento}>
                                <i className="bi bi-x-lg"></i> Cancelar
                            </button>
                        }
                    </div>
                </form>

                {/* Tabela */}
                <div className="he-card sem-rolagem">
                    <div className="cabecalho-pagina" style={{ marginBottom: "0.8em" }}>
                        <h5 className="he-titulo he-dourado" style={{ margin: 0 }}><i className="bi bi-list-ul"></i> Lançamentos</h5>
                        <span style={{ color: CINZA, fontSize: "0.85em" }}>
                            {dados.lancamentos.length} lançamento(s) no período
                        </span>
                    </div>

                    {carregandoLanc
                        ? <div style={{ textAlign: "center", padding: "2em" }}><span className="spinner-border" style={{ color: DOURADO }}></span></div>
                        : dados.lancamentos.length === 0
                            ? <p style={{ color: CINZA, textAlign: "center", padding: "1.5em", margin: 0 }}>
                                Nenhum lançamento com esses filtros. Registre o primeiro aí em cima 💛
                            </p>
                            : <div className="tabela-rolavel rolagem-so-desktop">
                                <table className="table table-hover tabela-responsiva tabela-mini">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Descrição</th>
                                        <th>Categoria</th>
                                        <th>Tipo</th>
                                        <th style={{ textAlign: "right" }}>Valor</th>
                                        <th>Forma</th>
                                        <th style={{ textAlign: "right" }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dados.lancamentos.map((l) => {
                                        const receita = l.tipo === "receita";
                                        return (
                                            <tr key={l.id} style={editandoLancamento === l.id ? { borderLeft: `3px solid ${DOURADO}`, background: "rgba(212, 175, 55, 0.1)" } : undefined}>
                                                <td data-rotulo="Data" style={{ whiteSpace: "nowrap" }}>{dataBR(l.data)}</td>
                                                <td className="principal ordem-primeiro" data-rotulo="Descrição" style={{ fontWeight: 600 }}>
                                                    {l.descricao}
                                                    {l.observacao && <div style={{ color: CINZA, fontSize: "0.8em", fontWeight: 400 }}>{l.observacao}</div>}
                                                </td>
                                                <td data-rotulo="Categoria"><span style={chip}>{rotuloCategoria(l.categoria)}</span></td>
                                                <td data-rotulo="Tipo">
                                                    <span className={`badge ${receita ? "badge-ativo" : "badge-atrasada"}`}>
                                                        {receita ? "Receita" : "Despesa"}
                                                    </span>
                                                </td>
                                                <td className="destaque" data-rotulo="Valor" style={{ textAlign: "right", fontWeight: 700, whiteSpace: "nowrap", color: receita ? VERDE : VERMELHO }}>
                                                    {receita ? "+" : "−"}{real(l.valor)}
                                                </td>
                                                <td data-rotulo="Forma" style={{ color: CINZA }}>{rotuloForma(l.formaPagamento)}</td>
                                                <td className="acoes">
                                                    <div className="acoes-linha" style={{ justifyContent: "flex-end" }}>
                                                        <button className="btn btn-sm btn-outline-he" title="Editar" onClick={() => editarLancamento(l)}>
                                                            <i className="bi bi-pencil"></i> <span className="so-mobile-inline">Editar</span>
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-danger" title="Excluir" onClick={() => handleExcluirLancamento(l)}>
                                                            <i className="bi bi-trash"></i> <span className="so-mobile-inline">Excluir</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                    }
                </div>
            </>
        );
    }

    // ===== ABA CONTAS =====
    function renderContas() {
        const r = contas.resumo || {};
        const hoje = hojeISO();
        const catAlvo = conta.tipo === "pagar" ? "despesa" : "receita";
        const lista = [...(contas.contas || [])].sort((a, b) =>
            String(a.vencimento || "").localeCompare(String(b.vencimento || ""))
        );

        const situacao = (c) => {
            if (c.status === "paga") return "paga";
            if (c.status === "cancelada") return "cancelada";
            const venc = String(c.vencimento || "").slice(0, 10);
            if (venc < hoje) return "vencida";
            if (venc === hoje) return "hoje";
            return "futura";
        };

        const bordaDaSituacao = {
            vencida: `3px solid ${VERMELHO}`,
            hoje: `3px solid ${DOURADO}`,
            paga: `3px solid ${VERDE}`,
            cancelada: `3px solid ${CINZA}`,
            futura: "3px solid transparent",
        };

        return (
            <>
                <div className="cards-estatistica" style={{ margin: "1.2em 0" }}>
                    {cardValor("A pagar", real(r.aPagar), VERMELHO, {
                        onClick: () => setFiltrosConta((f) => ({ ...f, tipo: f.tipo === "pagar" ? "" : "pagar" })),
                        title: "Clique pra ver só as contas a pagar",
                        estilo: {
                            cursor: "pointer",
                            border: filtrosConta.tipo === "pagar" ? `1px solid ${VERMELHO}` : undefined,
                        },
                    })}
                    {cardValor("A receber", real(r.aReceber), VERDE, {
                        onClick: () => setFiltrosConta((f) => ({ ...f, tipo: f.tipo === "receber" ? "" : "receber" })),
                        title: "Clique pra ver só as contas a receber",
                        estilo: {
                            cursor: "pointer",
                            border: filtrosConta.tipo === "receber" ? `1px solid ${VERDE}` : undefined,
                        },
                    })}
                    {/* o backend manda esses três como SOMA em R$ das contas abertas, não como contagem */}
                    {cardValor("Vencidas", real(r.vencidas), VERMELHO, {
                        estilo: Number(r.vencidas || 0) > 0
                            ? { border: `1px solid ${VERMELHO}`, animation: "he-pulsa 1.6s ease-in-out infinite" }
                            : undefined,
                        rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>em atraso</div>,
                    })}
                    {cardValor("Vence hoje", real(r.venceHoje), DOURADO, {
                        rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>{dataBR(hoje)}</div>,
                    })}
                    {cardValor("Vence na semana", real(r.venceSemana), DOURADO, {
                        classe: "largura-total",
                        rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>próximos 7 dias</div>,
                    })}
                </div>

                {/* Sub-filtros */}
                <div className="he-card" style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", marginBottom: "1em" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flex: "1 1 280px", minWidth: 0 }}>
                        <span style={{ color: CINZA, fontSize: "0.8em", textTransform: "uppercase", letterSpacing: 0.6 }}>Tipo</span>
                        <div className="filtros-status" style={{ flex: "1 1 auto" }}>
                            {[{ v: "", r: "Todas" }, { v: "pagar", r: "A pagar" }, { v: "receber", r: "A receber" }].map((o) => (
                                <button key={o.v || "todas"} type="button"
                                    className={filtrosConta.tipo === o.v ? "btn btn-sm btn-he" : "btn btn-sm btn-outline-he"}
                                    onClick={() => setFiltrosConta({ ...filtrosConta, tipo: o.v })}>
                                    {o.r}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flex: "1 1 280px", minWidth: 0 }}>
                        <span style={{ color: CINZA, fontSize: "0.8em", textTransform: "uppercase", letterSpacing: 0.6 }}>Status</span>
                        <div className="filtros-status" style={{ flex: "1 1 auto" }}>
                            {[{ v: "aberta", r: "Abertas" }, { v: "paga", r: "Pagas" }, { v: "", r: "Todas" }].map((o) => (
                                <button key={o.v || "todos"} type="button"
                                    className={filtrosConta.status === o.v ? "btn btn-sm btn-he" : "btn btn-sm btn-outline-he"}
                                    onClick={() => setFiltrosConta({ ...filtrosConta, status: o.v })}>
                                    {o.r}
                                </button>
                            ))}
                        </div>
                    </div>
                    <span style={{ marginLeft: "auto", color: CINZA, fontSize: "0.85em" }}>{lista.length} conta(s)</span>
                </div>

                {/* Nova conta */}
                <form onSubmit={handleSalvarConta} className="he-card form-mobile" style={{ marginBottom: "1em", display: "flex", flexDirection: "column", gap: 12 }}>
                    <h5 className="he-titulo he-dourado" style={{ margin: 0 }}>
                        <i className={editandoConta ? "bi bi-pencil-square" : "bi bi-calendar-plus"}></i>{" "}
                        {editandoConta ? "Editando conta" : "Nova conta"}
                    </h5>

                    {contaBaixada &&
                        <div style={{ fontSize: "0.8em", color: DOURADO }}>
                            <i className="bi bi-lock"></i> Conta já baixada: tipo, valor e vencimento só mudam depois do estorno.
                        </div>
                    }

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {botaoTipo(conta.tipo === "pagar", VERMELHO, () => mudarTipoConta("pagar"), "bi bi-arrow-up-circle", "A pagar", contaBaixada)}
                        {botaoTipo(conta.tipo === "receber", VERDE, () => mudarTipoConta("receber"), "bi bi-arrow-down-circle", "A receber", contaBaixada)}
                    </div>

                    <div className="row">
                        <div className="col-md-4" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Descrição</label>
                            <input className="form-control form-control-sm" placeholder="Ex: Aluguel da sala — agosto"
                                value={conta.descricao}
                                onChange={(e) => setConta({ ...conta, descricao: e.target.value })} required />
                        </div>
                        <div className="col-md-2" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Valor</label>
                            <input className="form-control form-control-sm" type="number" step="0.01" min="0.01" placeholder="0,00"
                                value={conta.valor} disabled={contaBaixada}
                                onChange={(e) => setConta({ ...conta, valor: e.target.value })} required />
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Vencimento</label>
                            <input className="form-control form-control-sm" type="date" value={conta.vencimento}
                                disabled={contaBaixada}
                                onChange={(e) => setConta({ ...conta, vencimento: e.target.value })} required />
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>
                                {conta.tipo === "pagar" ? "Fornecedor" : "Cliente"}
                            </label>
                            <input className="form-control form-control-sm"
                                placeholder={conta.tipo === "pagar" ? "Quem vai receber" : "Quem vai pagar"}
                                value={conta.pessoa}
                                onChange={(e) => setConta({ ...conta, pessoa: e.target.value })} />
                        </div>
                        <div className="col-md-4" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Categoria</label>
                            <select className="form-select form-select-sm" value={conta.categoria}
                                onChange={(e) => setConta({ ...conta, categoria: e.target.value })}>
                                {CATEGORIAS.filter((c) => c.tipo === catAlvo).map((c) =>
                                    <option key={c.valor} value={c.valor}>{c.rotulo}</option>
                                )}
                            </select>
                        </div>
                        <div className="col-md-5" style={{ marginBottom: 8 }}>
                            <label className="form-label he-dourado" style={rotulo}>Observação (opcional)</label>
                            <input className="form-control form-control-sm" placeholder="Nº do boleto, contato, combinado..."
                                value={conta.observacao}
                                onChange={(e) => setConta({ ...conta, observacao: e.target.value })} />
                        </div>
                        <div className="col-md-3" style={{ marginBottom: 8, display: "flex", alignItems: "flex-end" }}>
                            <div className="form-check" style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, margin: 0, padding: 0 }}>
                                <input className="form-check-input" type="checkbox" id="conta-recorrente"
                                    style={{ margin: 0, flexShrink: 0 }}
                                    checked={!!conta.recorrente}
                                    onChange={(e) => setConta({ ...conta, recorrente: e.target.checked })} />
                                <label className="form-check-label" htmlFor="conta-recorrente" style={{ fontSize: "0.85em", margin: 0, cursor: "pointer" }}>
                                    Repete todo mês
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="acoes-linha">
                        <button type="submit" className="btn btn-he" style={{ flex: "1 1 200px" }} disabled={salvandoConta}>
                            {salvandoConta
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <><i className="bi bi-check-lg"></i> {editandoConta ? "Salvar alteração" : "Cadastrar conta"}</>}
                        </button>
                        {editandoConta &&
                            <button type="button" className="btn btn-outline-he" onClick={cancelarEdicaoConta}>
                                <i className="bi bi-x-lg"></i> Cancelar
                            </button>
                        }
                    </div>
                </form>

                {/* Lista */}
                <div className="he-card sem-rolagem">
                    <h5 className="he-titulo he-dourado" style={{ marginBottom: "0.8em" }}>
                        <i className="bi bi-calendar-check"></i> Contas por vencimento
                    </h5>

                    {carregandoContas
                        ? <div style={{ textAlign: "center", padding: "2em" }}><span className="spinner-border" style={{ color: DOURADO }}></span></div>
                        : lista.length === 0
                            ? <p style={{ color: CINZA, textAlign: "center", padding: "1.5em", margin: 0 }}>
                                Nenhuma conta com esses filtros.
                            </p>
                            : <table className="table table-hover tabela-responsiva tabela-mini">
                                <thead>
                                    <tr>
                                        <th>Vencimento</th>
                                        <th>Descrição</th>
                                        <th>Fornecedor / Cliente</th>
                                        <th>Categoria</th>
                                        <th>Situação</th>
                                        <th style={{ textAlign: "right" }}>Valor</th>
                                        <th style={{ textAlign: "right" }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lista.map((c) => {
                                        const sit = situacao(c);
                                        const paga = sit === "paga";
                                        const cancelada = sit === "cancelada";
                                        const receber = c.tipo === "receber";
                                        return (
                                            <Fragment key={c.id}>
                                                <tr style={{ borderLeft: bordaDaSituacao[sit] }}>
                                                    <td data-rotulo="Vencimento" style={{ whiteSpace: "nowrap", fontWeight: 600, color: sit === "vencida" ? VERMELHO : sit === "hoje" ? DOURADO : undefined }}>
                                                        {dataBR(c.vencimento)}
                                                        {c.recorrente && <i className="bi bi-arrow-repeat" title="Conta recorrente" style={{ marginLeft: 6, color: CINZA }}></i>}
                                                    </td>
                                                    <td className="principal ordem-primeiro" data-rotulo="Descrição" style={{ fontWeight: 600 }}>
                                                        {c.descricao}
                                                        {c.observacao && <div style={{ color: CINZA, fontSize: "0.8em", fontWeight: 400 }}>{c.observacao}</div>}
                                                        {paga && c.dataPagamento &&
                                                            <div style={{ color: VERDE, fontSize: "0.78em", fontWeight: 400 }}>
                                                                Baixada em {dataBR(c.dataPagamento)}{c.valorPago ? ` — ${real(c.valorPago)}` : ""}
                                                            </div>
                                                        }
                                                    </td>
                                                    <td data-rotulo="Fornecedor / Cliente" style={{ color: CINZA }}>{c.pessoa || "—"}</td>
                                                    <td data-rotulo="Categoria"><span style={chip}>{rotuloCategoria(c.categoria)}</span></td>
                                                    <td data-rotulo="Situação">
                                                        <span className={`badge ${paga ? "badge-paga" : sit === "vencida" ? "badge-atrasada" : "badge-pendente"}`}
                                                            style={cancelada ? { background: "rgba(154,165,181,0.18)", color: CINZA, border: `1px solid ${CINZA}` } : undefined}>
                                                            {paga ? "Paga" : cancelada ? "Cancelada" : sit === "vencida" ? "Vencida" : sit === "hoje" ? "Vence hoje" : "Aberta"}
                                                        </span>
                                                    </td>
                                                    <td className="destaque" data-rotulo="Valor" style={{ textAlign: "right", fontWeight: 700, whiteSpace: "nowrap", color: receber ? VERDE : VERMELHO }}>
                                                        {receber ? "+" : "−"}{real(c.valor)}
                                                    </td>
                                                    <td className="acoes">
                                                        <div className="acoes-linha" style={{ justifyContent: "flex-end" }}>
                                                            {!paga && !cancelada &&
                                                                <button className="btn btn-sm btn-he" title="Registrar o pagamento" onClick={() => abrirBaixa(c)}>
                                                                    <i className="bi bi-check2-circle"></i> Dar baixa
                                                                </button>
                                                            }
                                                            {paga &&
                                                                <button className="btn btn-sm btn-outline-he" title="Desfazer a baixa" onClick={() => handleEstornar(c)}>
                                                                    <i className="bi bi-arrow-counterclockwise"></i> Estornar
                                                                </button>
                                                            }
                                                            <button className="btn btn-sm btn-outline-he" title="Editar" onClick={() => editarConta(c)}>
                                                                <i className="bi bi-pencil"></i> <span className="so-mobile-inline">Editar</span>
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-danger" title="Excluir" onClick={() => handleExcluirConta(c)}>
                                                                <i className="bi bi-trash"></i> <span className="so-mobile-inline">Excluir</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {baixa?.id === c.id &&
                                                    <tr style={{ borderLeft: `3px solid ${DOURADO}` }}>
                                                        <td className="acoes" colSpan={7} style={{ background: "rgba(212, 175, 55, 0.08)", textAlign: "left" }}>
                                                            <form onSubmit={handleBaixar} className="form-mobile">
                                                                <div className="row" style={{ alignItems: "flex-end" }}>
                                                                    <div className="col-md-3" style={{ marginBottom: 8 }}>
                                                                        <label className="form-label he-dourado" style={rotulo}>Data do pagamento</label>
                                                                        <input className="form-control form-control-sm" type="date" value={baixa.data}
                                                                            onChange={(e) => setBaixa({ ...baixa, data: e.target.value })} required />
                                                                    </div>
                                                                    <div className="col-md-3" style={{ marginBottom: 8 }}>
                                                                        <label className="form-label he-dourado" style={rotulo}>Valor pago</label>
                                                                        <input className="form-control form-control-sm" type="number" step="0.01" min="0.01"
                                                                            value={baixa.valorPago}
                                                                            onChange={(e) => setBaixa({ ...baixa, valorPago: e.target.value })} required />
                                                                    </div>
                                                                    <div className="col-md-3" style={{ marginBottom: 8 }}>
                                                                        <label className="form-label he-dourado" style={rotulo}>Forma de pagamento</label>
                                                                        <select className="form-select form-select-sm" value={baixa.formaPagamento}
                                                                            onChange={(e) => setBaixa({ ...baixa, formaPagamento: e.target.value })}>
                                                                            {FORMAS.map((f) => <option key={f.valor} value={f.valor}>{f.rotulo}</option>)}
                                                                        </select>
                                                                    </div>
                                                                    <div className="col-md-3" style={{ marginBottom: 8 }}>
                                                                        <div className="acoes-linha">
                                                                            <button type="submit" className="btn btn-sm btn-he" disabled={dandoBaixa}>
                                                                                {dandoBaixa
                                                                                    ? <span className="spinner-border spinner-border-sm"></span>
                                                                                    : <><i className="bi bi-check-lg"></i> Confirmar baixa</>}
                                                                            </button>
                                                                            <button type="button" className="btn btn-sm btn-outline-he" onClick={() => setBaixa(null)}>
                                                                                <i className="bi bi-x-lg"></i> Cancelar
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </form>
                                                        </td>
                                                    </tr>
                                                }
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                    }
                </div>
            </>
        );
    }

    // ===== ABA FECHAMENTO =====
    function renderFechamento() {
        if (carregandoFech || !fechamento) {
            return <div style={{ textAlign: "center", padding: "3em" }}><span className="spinner-border" style={{ color: DOURADO }}></span></div>;
        }

        const receitas = Number(fechamento.receitas || 0);
        const despesas = Number(fechamento.despesas || 0);
        const resultado = Number(fechamento.resultado ?? receitas - despesas);
        const anterior = fechamento.comparativo?.mesAnterior || { receitas: 0, despesas: 0, resultado: 0 };
        const porDia = fechamento.porDia || [];
        const porCategoria = fechamento.porCategoria || [];
        const abertas = fechamento.contasAbertas || { aPagar: 0, aReceber: 0 };

        // pico real do mês (pode ser 0 num mês sem lançamento); a base da divisão
        // nunca pode ser 0, senão a barra sai com height: "NaN%"
        const picoDia = porDia.reduce(
            (maior, d) => Math.max(maior, Number(d.receitas || 0), Number(d.despesas || 0)),
            0
        );
        const altura = (v) => `${(Number(v || 0) / (picoDia || 1)) * 100}%`;

        const catsReceita = porCategoria.filter((c) => c.tipo === "receita");
        const catsDespesa = porCategoria.filter((c) => c.tipo === "despesa");

        // "subir é bom" pra receita e resultado; pra despesa é o contrário
        function comparativo(atual, base, subirEhBom) {
            const v = variacao(atual, base);
            if (v === null) {
                return <div style={{ fontSize: "0.72em", color: CINZA }}>sem base no mês anterior</div>;
            }
            const subiu = v >= 0;
            const bom = subirEhBom ? subiu : !subiu;
            return (
                <div style={{ fontSize: "0.75em", fontWeight: 700, color: v === 0 ? CINZA : bom ? VERDE : VERMELHO }}>
                    {v === 0 ? "=" : subiu ? "▲" : "▼"} {pct(v)}
                    <span style={{ color: CINZA, fontWeight: 400 }}> vs. mês anterior</span>
                </div>
            );
        }

        function blocoCategorias(titulo, itens, cor) {
            const picoCat = itens.reduce((maior, c) => Math.max(maior, Number(c.total || 0)), 0) || 1;
            return (
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                    <h6 className="he-titulo" style={{ color: cor, marginBottom: 8 }}>{titulo}</h6>
                    {itens.length === 0
                        ? <p style={{ color: CINZA, fontSize: "0.85em", margin: 0 }}>Nada lançado no mês.</p>
                        : <table className="table table-sm tabela-responsiva tabela-mini">
                            <thead>
                                <tr>
                                    <th>Categoria</th>
                                    <th>Tipo</th>
                                    <th style={{ width: "38%" }}>Participação</th>
                                    <th style={{ textAlign: "right" }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itens.map((c) => (
                                    <tr key={`${c.tipo}-${c.categoria}`}>
                                        <td className="principal ordem-primeiro" data-rotulo="Categoria">{rotuloCategoria(c.categoria)}</td>
                                        <td data-rotulo="Tipo">
                                            <span className={`badge ${c.tipo === "receita" ? "badge-ativo" : "badge-atrasada"}`}>
                                                {c.tipo === "receita" ? "Receita" : "Despesa"}
                                            </span>
                                        </td>
                                        <td data-rotulo="Participação">
                                            <div style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.08)", borderRadius: 6, height: 10, overflow: "hidden" }}>
                                                <div style={{
                                                    width: `${(Number(c.total || 0) / picoCat) * 100}%`,
                                                    height: "100%", background: cor, borderRadius: 6,
                                                }}></div>
                                            </div>
                                        </td>
                                        <td className="destaque" data-rotulo="Total" style={{ textAlign: "right", fontWeight: 700, color: cor, whiteSpace: "nowrap" }}>{real(c.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    }
                </div>
            );
        }

        return (
            <>
                <div className="he-card" style={{ margin: "1.2em 0", textAlign: "center" }}>
                    <h4 style={{ fontWeight: 700, margin: 0, fontSize: "clamp(1.05rem, 4.4vw, 1.5rem)", lineHeight: 1.35 }}>
                        HELP <span className="he-dourado">EMPRESARIAL</span> — Fechamento de {nomeDoMes(fechamento.mes || mes)}
                    </h4>
                    <div style={{ color: CINZA, fontSize: "0.8em" }}>
                        Comparado com {nomeDoMes(mesAnteriorDe(fechamento.mes || mes))}
                    </div>
                </div>

                <div className="cards-estatistica" style={{ marginBottom: "1em" }}>
                    {cardValor("Receitas", real(receitas), VERDE, { rodape: comparativo(receitas, anterior.receitas, true) })}
                    {cardValor("Despesas", real(despesas), VERMELHO, { rodape: comparativo(despesas, anterior.despesas, false) })}
                    {cardValor("Resultado do mês", real(resultado), resultado >= 0 ? VERDE : VERMELHO, {
                        classe: "largura-total",
                        estilo: { border: `1px solid ${DOURADO}66` },
                        rodape: comparativo(resultado, anterior.resultado, true),
                    })}
                </div>

                {/* Gráfico dia a dia */}
                <div className="he-card sem-rolagem" style={{ marginBottom: "1em" }}>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: "1em" }}>
                        <h5 className="he-titulo he-dourado" style={{ margin: 0 }}><i className="bi bi-bar-chart"></i> Dia a dia</h5>
                        <span style={{ fontSize: "0.78em", color: CINZA }}>
                            <span style={{ display: "inline-block", width: 10, height: 10, background: VERDE, borderRadius: 2, marginRight: 5 }}></span>
                            Receitas
                            <span style={{ display: "inline-block", width: 10, height: 10, background: VERMELHO, borderRadius: 2, margin: "0 5px 0 14px" }}></span>
                            Despesas
                        </span>
                        <span style={{ marginLeft: "auto", fontSize: "0.78em", color: CINZA }}>pico do mês: {real(picoDia)}</span>
                    </div>

                    {/* o backend devolve TODO dia do mês, mesmo zerado — quem diz se
                        o mês está vazio é o pico, não o tamanho da lista */}
                    {picoDia === 0
                        ? <p style={{ color: CINZA, textAlign: "center", margin: 0 }}>Nenhum lançamento nesse mês.</p>
                        : <div className="grafico-fluido grafico-fluido-mes">
                            <div className="grafico-fluido-trilho" style={{ "--trilho-min": `${porDia.length * 26}px` }}>
                                {porDia.map((d, i) => (
                                    <div key={d.data} className="grafico-fluido-coluna"
                                        title={`${dataBR(d.data)} — Receitas ${real(d.receitas)} · Despesas ${real(d.despesas)}`}>
                                        <div className="grafico-fluido-par" style={{ height: 150 }}>
                                            <div className="grafico-fluido-barra" style={{
                                                height: altura(d.receitas),
                                                minHeight: Number(d.receitas || 0) > 0 ? 3 : 0,
                                                background: `linear-gradient(180deg, #43d17c 0%, ${VERDE} 100%)`,
                                            }}></div>
                                            <div className="grafico-fluido-barra" style={{
                                                height: altura(d.despesas),
                                                minHeight: Number(d.despesas || 0) > 0 ? 3 : 0,
                                                background: `linear-gradient(180deg, ${VERMELHO} 0%, #c0392b 100%)`,
                                            }}></div>
                                        </div>
                                        {/* no celular só o 5º dia mostra o número — 31 rótulos não cabem */}
                                        <div className={`grafico-fluido-dia${(i + 1) % 5 === 0 ? "" : " alternado"}`}>
                                            {String(d.data).slice(8, 10)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                </div>

                {/* Por categoria */}
                <div className="he-card sem-rolagem" style={{ marginBottom: "1em" }}>
                    <h5 className="he-titulo he-dourado" style={{ marginBottom: "1em" }}>
                        <i className="bi bi-pie-chart"></i> Por categoria
                    </h5>
                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                        {blocoCategorias("Receitas", catsReceita, VERDE)}
                        {blocoCategorias("Despesas", catsDespesa, VERMELHO)}
                    </div>
                </div>

                {/* Em aberto */}
                <div className="he-card sem-rolagem">
                    <h5 className="he-titulo he-dourado" style={{ marginBottom: "0.8em" }}>
                        <i className="bi bi-hourglass-split"></i> Em aberto
                    </h5>
                    <div className="cards-estatistica">
                        {cardValor("A pagar", real(abertas.aPagar), VERMELHO, {
                            rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>ainda não baixadas</div>,
                        })}
                        {cardValor("A receber", real(abertas.aReceber), VERDE, {
                            rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>ainda não recebidas</div>,
                        })}
                        {cardValor(
                            "Previsto no caixa",
                            real(Number(abertas.aReceber || 0) - Number(abertas.aPagar || 0)),
                            Number(abertas.aReceber || 0) - Number(abertas.aPagar || 0) >= 0 ? VERDE : VERMELHO,
                            {
                                classe: "largura-total",
                                rodape: <div style={{ fontSize: "0.72em", color: CINZA }}>a receber − a pagar</div>,
                            }
                        )}
                    </div>
                </div>
            </>
        );
    }

    const ABAS = [
        { chave: "lancamentos", rotulo: "Lançamentos", icone: "bi bi-journal-text" },
        { chave: "contas", rotulo: "Contas a pagar/receber", icone: "bi bi-calendar-check" },
        { chave: "fechamento", rotulo: "Fechamento mensal", icone: "bi bi-clipboard-data" },
    ];

    return (
        <div className="pagina">
            <style>{`
                @keyframes he-pulsa {
                    0%, 100% { box-shadow: 0 0 0 rgba(231, 76, 60, 0); }
                    50% { box-shadow: 0 0 20px rgba(231, 76, 60, 0.75); }
                }
            `}</style>

            <div className="nao-imprimir cabecalho-pagina">
                <h2 className="he-titulo" style={{ margin: 0 }}>Contabilidade <span className="he-dourado">— o dinheiro da casa</span></h2>
                {aba === "fechamento" &&
                    <div className="form-mobile" style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                            <label className="form-label he-dourado" style={rotulo}>Mês</label>
                            <input className="form-control form-control-sm" type="month" value={mes}
                                onChange={(e) => setMes(e.target.value || MES_ATUAL)} />
                        </div>
                        <button className="btn btn-outline-he btn-sm btn-total-mobile" onClick={() => window.print()}>
                            <i className="bi bi-printer"></i> Imprimir / PDF
                        </button>
                    </div>
                }
            </div>

            {/* Abas — chips compactos */}
            <div className="nao-imprimir filtros-status" style={{ margin: "1em 0 0" }}>
                {ABAS.map((a) => (
                    <button key={a.chave} type="button"
                        className={aba === a.chave ? "btn btn-he btn-sm" : "btn btn-outline-he btn-sm"}
                        style={{ borderRadius: 20, padding: "0.45em 1.1em", fontWeight: 700 }}
                        onClick={() => setAba(a.chave)}>
                        <i className={a.icone}></i> {a.rotulo}
                    </button>
                ))}
            </div>

            {aba === "lancamentos" && renderLancamentos()}
            {aba === "contas" && renderContas()}
            {aba === "fechamento" && renderFechamento()}
        </div>
    );
}
